'use client'

import { deleteDB } from 'idb'
import { useCallback, useEffect } from 'react'

import useLogger from '~/debug/logger'

import { DB_PREFIX, getCacheDbName } from './DataManager'

async function getDatabases() {
  try {
    return await indexedDB.databases()
  } catch (error) {
    return []
  }
}

function useDeleteDatabases() {
  const logger = useLogger('useDeleteDatabases')

  return useCallback(
    async (currentDbName?: string) => {
      const databases = await getDatabases()

      for (const db of databases) {
        if (db.name?.startsWith(DB_PREFIX) && db.name !== currentDbName) {
          try {
            await deleteDB(db.name)
            logger.info('[useDeleteDatabases] deleted database', db.name)
          } catch (error) {
            logger.error('[useDeleteDatabases] failed to delete database', error)
          }
        }
      }
    },
    [logger]
  )
}

export type UseIndexedDbCleanupProps = {
  isUnauthenticated: boolean
  userId?: string
  /** Demo mode uses a separate cache database; pass the flag so it isn't swept while active. */
  isDemoMode?: boolean
}

/**
 * Deletes the application's IndexedDB when `isUnauthenticated` is true.
 */
export default function useIndexedDbCleanup({ isUnauthenticated, userId, isDemoMode }: UseIndexedDbCleanupProps) {
  const deleteDatabases = useDeleteDatabases()
  const logger = useLogger('useIndexedDbCleanup')

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isUnauthenticated) {
      // Clean up all databases
      logger.info('cleaning up all databases')
      void deleteDatabases()
    } else if (userId) {
      // Clean up databases other than the active one: other users' caches (fallback for
      // logout cleanup) and this user's counterpart cache from the other demo-mode state.
      logger.info('cleaning up databases for user', userId)
      void deleteDatabases(getCacheDbName(userId, !!isDemoMode))
    }
  }, [deleteDatabases, isDemoMode, isUnauthenticated, logger, userId])
}
